import { FC, useState } from 'react'
import {
  Datagrid, DateField, FilterList, FilterListItem, FunctionField, List,
  TextField, useGetList, useRecordContext, useTranslate,
} from 'react-admin'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import SourceIcon from '@mui/icons-material/Source'
import BusinessIcon from '@mui/icons-material/Business'
import { IconButton, Tooltip } from '@mui/material'
import { EnabledChip, FilterAside, ToolSourceChip } from '../../components'
import { ApplicationOut, ProxyOut } from '../../types/apiRequests'
import { getPerPage } from '../../utils/usePerPage'

const CopyUrlButton: FC = () => {
  const record = useRecordContext<ProxyOut>()
  const translate = useTranslate()
  const [copied, setCopied] = useState<string | null>(null)

  if (!record) return null

  const proxyUrl = `${window.location.origin}/api/v1/p/${record.name}/`

  const handleCopy = () => {
    void navigator.clipboard.writeText(proxyUrl)
    setCopied(record.name)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <Tooltip title={copied === record.name ? translate('data.copied') : translate('data.copyUrl')}>
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopy() }}>
        {copied === record.name
          ? <CheckCircleOutlineIcon fontSize="small" color="success" />
          : <ContentCopyIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  )
}

const ProxyFilters: FC = () => {
  const translate = useTranslate()
  const { data: applications } = useGetList<ApplicationOut>(
    'application',
    { pagination: { page: 1, perPage: 100 }, sort: { field: 'display_name', order: 'ASC' } },
  )

  return (
    <FilterAside>
      <FilterList label={translate('filters.status')} icon={<PowerSettingsNewIcon />}>
        <FilterListItem label="filters.active" value={{ enabled: true }} />
        <FilterListItem label="filters.inactive" value={{ enabled: false }} />
      </FilterList>

      <FilterList label={translate('filters.toolSource')} icon={<SourceIcon />}>
        {/* eslint-disable camelcase */}
        <FilterListItem label="data.toolSourceOpenapi" value={{ tool_source: 'openapi' }} />
        <FilterListItem label="data.toolSourceManual" value={{ tool_source: 'manual' }} />
        {/* eslint-enable camelcase */}
      </FilterList>

      <FilterList label={translate('filters.application')} icon={<BusinessIcon />}>
        {applications?.map((app) => (
          <FilterListItem
            key={app.id}
            label={app.display_name}
            // eslint-disable-next-line camelcase
            value={{ application_id: app.id }}
          />
        ))}
      </FilterList>
    </FilterAside>
  )
}

const ProxyList: FC = () => (
  <List exporter={false} sort={{ field: 'created', order: 'DESC' }} perPage={getPerPage()} aside={<ProxyFilters />}>
    <Datagrid bulkActionButtons={false} rowClick="show">
      <TextField source="display_name" label="data.displayName" />
      <FunctionField<ProxyOut> label="data.toolSource" render={(record) => <ToolSourceChip source={record.tool_source} />} />
      <FunctionField label="data.enabled" render={() => <EnabledChip />} />
      <DateField source="created" showTime label="data.createdAt" />
      <CopyUrlButton />
    </Datagrid>
  </List>
)

export default ProxyList
